import { placeholderName } from '@core/utils/recipe-placeholder-pattern';
import { sameJson } from '@core/utils/shared-utils';

import {
  FILTER_OP_SHAPES,
  FieldPath,
  FilterExpr,
  FilterLiteral,
  FilterOp,
  FilterOpName,
  LabelLiteral,
  LabelLiterals,
  LabelRange,
  LabelText,
  LiteralRange,
  RelativeDateKeyword,
  makeOp,
  opArgument,
  opName
} from './wire';

export type HoleKind = 'text' | 'date' | 'date-time' | 'duration' | 'list';

export interface Hole {
  name: string;
  kind: HoleKind;
  location: string;
}

export type HoleIssueCode = 'malformed' | 'wrong-slot' | 'mixed-with-literals' | 'conflict';

export interface HoleIssue {
  code: HoleIssueCode;
  location: string;
  name?: string;
}

export interface HoleScan {
  holes: Hole[];
  issues: HoleIssue[];
}

/** Operators whose text argument may hold a hole; `has_label`'s argument is a label key and may not. */
const TEXT_SLOT_OPS: ReadonlySet<FilterOpName> = new Set([
  'contains',
  'not_contains',
  'starts_with',
  'ends_with',
  'matches_regex',
  'has_tag',
  'not_has_tag'
]);

export function holeName(value: string): string | null {
  return placeholderName(value) ?? null;
}

/**
 * Called for every string of the tree. `kind` is the hole kind the slot accepts, `null` when the slot
 * may not hold one. `list` is called for every list slot whose sole element may stand for the list.
 */
interface SlotVisitor {
  scalar(value: string, kind: HoleKind | null, location: string): string;
  list<T>(
    items: readonly T[],
    textOf: (item: T) => string | null,
    withText: (item: T, text: string) => T,
    location: string
  ): T[];
}

function literalText(literal: FilterLiteral): string | null {
  if (literal !== 'null' && 'string' in literal) return literal.string;
  if (literal !== 'null' && 'uid' in literal) return literal.uid;
  return null;
}

function literalWithText(literal: FilterLiteral, text: string): FilterLiteral {
  return literal !== 'null' && 'uid' in literal ? { uid: text } : { string: text };
}

const identity = (value: string): string => value;
const replaceText = (_value: string, text: string): string => text;

function visitPath(path: FieldPath, visitor: SlotVisitor, location: string): FieldPath {
  return {
    segments: path.segments.map((segment, index) =>
      'field' in segment
        ? {
            field: {
              name: visitor.scalar(segment.field.name, null, `${location}/${index}`)
            }
          }
        : {
            collection: {
              ...segment.collection,
              name: visitor.scalar(segment.collection.name, null, `${location}/${index}`)
            }
          }
    )
  };
}

/** `inList`: the literal is an element of a list, where only a sole text/uid hole is allowed. */
function visitLiteral(
  literal: FilterLiteral,
  visitor: SlotVisitor,
  location: string,
  inList = false
): FilterLiteral {
  const slot = (kind: HoleKind): HoleKind | null => (inList ? null : kind);
  if (literal === 'null') return literal;
  if ('string' in literal)
    return { string: visitor.scalar(literal.string, slot('text'), location) };
  if ('uid' in literal) return { uid: visitor.scalar(literal.uid, slot('text'), location) };
  if ('date' in literal)
    return { date: visitor.scalar(literal.date, slot('date'), location) };
  if ('date_time' in literal) {
    return { date_time: visitor.scalar(literal.date_time, slot('date-time'), location) };
  }
  if ('duration' in literal) {
    return { duration: visitor.scalar(literal.duration, slot('duration'), location) };
  }
  if ('uid_list' in literal) {
    return { uid_list: visitor.list(literal.uid_list, identity, replaceText, location) };
  }
  if ('list' in literal) return { list: visitLiterals(literal.list, visitor, location) };
  if ('self' in literal) {
    return 'label' in literal.self
      ? { self: { label: visitor.scalar(literal.self.label, null, location) } }
      : { self: { field: visitPath(literal.self.field, visitor, location) } };
  }
  return literal;
}

function visitLiterals(
  items: FilterLiteral[],
  visitor: SlotVisitor,
  location: string
): FilterLiteral[] {
  return visitor
    .list(items, literalText, literalWithText, location)
    .map((item, index) =>
      literalText(item) === null
        ? visitLiteral(item, visitor, `${location}/${index}`, true)
        : item
    );
}

function visitOp(op: FilterOp, visitor: SlotVisitor, location: string): FilterOp {
  const name = opName(op);
  const arg = opArgument(op);
  const at = `${location}/${name}`;
  switch (FILTER_OP_SHAPES[name]) {
    case 'none':
      return op;
    case 'relative_date':
      // A stored recipe may carry a hole where the typed spec expects a keyword.
      return typeof arg === 'string'
        ? makeOp(name, visitor.scalar(arg, null, at) as RelativeDateKeyword)
        : op;
    case 'literal':
      return makeOp(name, visitLiteral(arg as FilterLiteral, visitor, at));
    case 'range': {
      const range = arg as LiteralRange;
      return makeOp(name, {
        lo: visitLiteral(range.lo, visitor, `${at}/lo`),
        hi: visitLiteral(range.hi, visitor, `${at}/hi`)
      });
    }
    case 'literals':
      return makeOp(name, visitLiterals(arg as FilterLiteral[], visitor, at));
    case 'text':
      return makeOp(
        name,
        visitor.scalar(arg as string, TEXT_SLOT_OPS.has(name) ? 'text' : null, at)
      );
    case 'texts':
      return makeOp(name, visitor.list(arg as string[], identity, replaceText, at));
    case 'label_literal': {
      const value = arg as LabelLiteral;
      return makeOp(name, {
        label: visitor.scalar(value.label, null, `${at}/label`),
        value: visitLiteral(value.value, visitor, `${at}/value`)
      });
    }
    case 'label_literals': {
      const value = arg as LabelLiterals;
      return makeOp(name, {
        label: visitor.scalar(value.label, null, `${at}/label`),
        values: visitLiterals(value.values, visitor, `${at}/values`)
      });
    }
    case 'label_range': {
      const value = arg as LabelRange;
      return makeOp(name, {
        label: visitor.scalar(value.label, null, `${at}/label`),
        lo: visitLiteral(value.lo, visitor, `${at}/lo`),
        hi: visitLiteral(value.hi, visitor, `${at}/hi`)
      });
    }
    case 'label_text': {
      const value = arg as LabelText;
      return makeOp(name, {
        label: visitor.scalar(value.label, null, `${at}/label`),
        value: visitor.scalar(value.value, 'text', `${at}/value`)
      });
    }
    default:
      // An operator the data layer added after this build is kept as written. Its argument is
      // handed over as one slot no hole may fill, so a `{{` inside it still counts as a hole.
      visitor.scalar(JSON.stringify(op), null, at);
      return op;
  }
}

function visitExpr(expr: FilterExpr, visitor: SlotVisitor, location: string): FilterExpr {
  if ('and' in expr)
    return {
      and: expr.and.map((item, i) => visitExpr(item, visitor, `${location}/and/${i}`))
    };
  if ('or' in expr)
    return {
      or: expr.or.map((item, i) => visitExpr(item, visitor, `${location}/or/${i}`))
    };
  if ('not' in expr) return { not: visitExpr(expr.not, visitor, `${location}/not`) };
  if ('predicate' in expr) {
    const at = `${location}/predicate`;
    return {
      predicate: {
        path: visitPath(expr.predicate.path, visitor, `${at}/path`),
        op: visitOp(expr.predicate.op, visitor, `${at}/op`)
      }
    };
  }
  const at = `${location}/association`;
  return {
    association: {
      edge: visitor.scalar(expr.association.edge, null, `${at}/edge`),
      quantifier: expr.association.quantifier,
      filter: visitExpr(expr.association.filter, visitor, `${at}/filter`)
    }
  };
}

export function scanHoles(expr: FilterExpr): HoleScan {
  const scan: HoleScan = { holes: [], issues: [] };
  const record = (value: string, kind: HoleKind | null, location: string): void => {
    if (!value.includes('{{')) return;
    const name = holeName(value);
    if (!name) scan.issues.push({ code: 'malformed', location });
    else if (kind === null) scan.issues.push({ code: 'wrong-slot', location, name });
    else scan.holes.push({ name, kind, location });
  };
  visitExpr(
    expr,
    {
      scalar(value, kind, location) {
        record(value, kind, location);
        return value;
      },
      list(items, textOf, _withText, location) {
        const texts = items.map(textOf);
        const sole = items.length === 1 && texts[0] !== null ? holeName(texts[0]) : null;
        if (sole) {
          scan.holes.push({ name: sole, kind: 'list', location });
        } else {
          texts.forEach((text, index) => {
            if (text === null || !text.includes('{{')) return;
            const name = holeName(text);
            scan.issues.push(
              name
                ? { code: 'mixed-with-literals', location: `${location}/${index}`, name }
                : { code: 'malformed', location: `${location}/${index}` }
            );
          });
        }
        return [...items];
      }
    },
    ''
  );
  const kinds = new Map<string, HoleKind>();
  for (const hole of scan.holes) {
    const seen = kinds.get(hole.name);
    if (seen === undefined) kinds.set(hole.name, hole.kind);
    else if (
      seen !== hole.kind &&
      !scan.issues.some((i) => i.code === 'conflict' && i.name === hole.name)
    ) {
      scan.issues.push({ code: 'conflict', location: hole.location, name: hole.name });
    }
  }
  return scan;
}

/** Each hole name once, with the kind of its first slot. */
export function distinctHoles(expr: FilterExpr): { name: string; kind: HoleKind }[] {
  const kinds = new Map<string, HoleKind>();
  for (const hole of scanHoles(expr).holes) {
    if (!kinds.has(hole.name)) kinds.set(hole.name, hole.kind);
  }
  return [...kinds].map(([name, kind]) => ({ name, kind }));
}

/**
 * Fills every hole named in `values`. A caller with "nothing chosen" must leave the name out of `values`
 * (treat it as unfilled); an empty array for a scalar slot throws rather than keep the placeholder.
 */
export function substituteHoles(
  expr: FilterExpr,
  values: Record<string, string | string[]>
): FilterExpr {
  const has = (name: string | null): name is string =>
    name !== null && Object.prototype.hasOwnProperty.call(values, name);
  return visitExpr(
    expr,
    {
      scalar(value, kind) {
        const name = kind === null ? null : holeName(value);
        if (!has(name)) return value;
        const filled = values[name];
        if (!Array.isArray(filled)) return filled;
        if (filled.length === 0)
          throw new Error(`Hole {{${name}}} was given an empty list for a single value`);
        return filled[0];
      },
      list(items, textOf, withText) {
        const text = items.length === 1 ? textOf(items[0]) : null;
        const name = text === null ? null : holeName(text);
        if (!has(name)) return [...items];
        const filled = values[name];
        return (Array.isArray(filled) ? filled : [filled]).map((entry) =>
          withText(items[0], entry)
        );
      }
    },
    ''
  );
}

/**
 * What becomes of a related-entity block whose every condition held an unfilled hole: kept as "has a
 * related row", or dropped like any other emptied node.
 */
export type EmptiedAssociation = 'any-related' | 'drop';

export function withoutUnfilledHoles(
  expr: FilterExpr,
  filled: ReadonlySet<string>,
  emptiedAssociation: EmptiedAssociation = 'any-related'
): FilterExpr | undefined {
  const without = (item: FilterExpr): FilterExpr | undefined =>
    withoutUnfilledHoles(item, filled, emptiedAssociation);
  if ('and' in expr || 'or' in expr) {
    const stored = 'and' in expr ? expr.and : expr.or;
    // Only a group emptied by dropping holes goes: a stored `any()` matches nothing and must stay so.
    if (stored.length === 0) return expr;
    const items = stored
      .map(without)
      .filter((item): item is FilterExpr => item !== undefined);
    if (items.length === 0) return undefined;
    if (items.length === 1) return items[0];
    return 'and' in expr ? { and: items } : { or: items };
  }
  if ('not' in expr) {
    // Whatever leaving a hole out does to the operand, negating it does the opposite: a dropped
    // `and` child or an emptied association would narrow the negation. So it goes as a whole.
    const inner = without(expr.not);
    return inner !== undefined && sameJson(inner, expr.not) ? expr : undefined;
  }
  if ('association' in expr) {
    if (expr.association.edge.includes('{{')) return undefined;
    const inner = without(expr.association.filter);
    if (inner === undefined && emptiedAssociation === 'drop') return undefined;
    return { association: { ...expr.association, filter: inner ?? { and: [] } } };
  }
  // A refused or malformed hole is never a fillable parameter, so its condition cannot be previewed.
  const scan = scanHoles(expr);
  return scan.issues.length === 0 && scan.holes.every((hole) => filled.has(hole.name))
    ? expr
    : undefined;
}
